import type {
	AssistantLogTranscriptDto,
	AssistantLogTranscriptQuery,
	AssistantLogUserDetailDto,
	AssistantLogUserDetailQuery,
	AssistantLogUsersPageDto,
	AssistantLogUsersQuery,
} from "@/types/ai-assistant-log";
import { request } from "./ai-assistant.api";

/**
 * Fetcher "Log Percakapan" (admin, baca saja). Semua path relatif ke
 * `/api/admin/ai-assistant` lewat `request` yang sudah menangani 404
 * (endpoint belum ada di server) dan pesan error berbahasa manusia.
 */

/** `403` dari endpoint transkrip: admin belum punya izin baca isi percakapan. */
export class TranscriptForbiddenError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "TranscriptForbiddenError";
	}
}

function queryString(
	params: Record<string, string | number | boolean | undefined>,
): string {
	const search = new URLSearchParams();
	for (const [key, value] of Object.entries(params))
		if (value !== undefined && value !== "") search.set(key, String(value));
	const qs = search.toString();
	return qs ? `?${qs}` : "";
}

/** Lapis 1: agregat per pengguna + ringkasan rentang. */
export function fetchLogUsers(
	q: AssistantLogUsersQuery,
	signal?: AbortSignal,
): Promise<AssistantLogUsersPageDto> {
	return request<AssistantLogUsersPageDto>(
		`/log/users${queryString({ ...q })}`,
		{ signal },
		"Memuat log percakapan",
	);
}

/** Lapis 2: profil + percakapan satu pengguna. */
export function fetchLogUserDetail(
	userId: string,
	q: AssistantLogUserDetailQuery,
	signal?: AbortSignal,
): Promise<AssistantLogUserDetailDto> {
	return request<AssistantLogUserDetailDto>(
		`/log/users/${encodeURIComponent(userId)}${queryString({ ...q })}`,
		{ signal },
		"Memuat percakapan pengguna",
	);
}

/**
 * Lapis 3: transkrip percakapan. 403 diterjemahkan menjadi
 * `TranscriptForbiddenError` supaya halaman bisa menampilkan ajakan
 * menyalakan izin, bukan pesan error mentah.
 */
export async function fetchLogTranscript(
	conversationId: string,
	q: AssistantLogTranscriptQuery,
	signal?: AbortSignal,
): Promise<AssistantLogTranscriptDto> {
	try {
		return await request<AssistantLogTranscriptDto>(
			`/log/conversations/${encodeURIComponent(conversationId)}${queryString({ ...q })}`,
			{ signal },
			"Memuat transkrip percakapan",
		);
	} catch (err) {
		const message = err instanceof Error ? err.message : "";
		if (message.includes("(403)")) throw new TranscriptForbiddenError(message);
		throw err;
	}
}
